import { useTranslations } from '@vocab/react';
import {
  Alert,
  Box,
  Stack,
  Strong,
  Text,
  TextLinkButton,
  useToast,
} from 'braid-design-system';
import debounce from 'lodash.debounce';
import { observer } from 'mobx-react';
import { type FC, useEffect, type ReactElement } from 'react';
import { NumberParam, useQueryParam } from 'use-query-params';

import { EmptyState } from 'src/App/CandidateManagementPage/EmptyState';
import type { FilterFns } from 'src/components/app/FilterSideBar/useFilters';
import Pagination from 'src/components/common/Pagination';
import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';
import transformProfileToComponentProps from 'src/components/details/DetailsContentView/Profile/service/transformProfile';
import { mapToSimplifiedAnswers } from 'src/components/list/JobApplicationCard/RoleRequirementsView/RoleRequirement/mapToSimplifiedAnswers';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import {
  TRACKING_CANDIDATE_DETAILS,
  TRACKING_CANDIDATE_LIST,
} from 'src/constants/pageTrackingData';
import { JOB_APPLICATION_TAB } from 'src/constants/tabs';
import { STATUS_CHANGE_LOCATION } from 'src/graphql/hirer/updateApplicationsStatus';
import { useDeepLinkedDetailView } from 'src/pages/CandidateManagementPage/utils/useDeepLinkedDetailView';
import type { FiltersEventCaptureData } from 'src/services/jobApplicationService/buildCaptureEventData';
import sendCandidateListView from 'src/services/jobApplicationService/sendCandidateListView';
import { BUCKETS, type Bucket } from 'src/stores/JobApplicationStore/types';
import { SUPPORT_BACK_BUTTON } from 'src/utils/buildHandleEnterAndSpaceKeyPress';
import { convertToIsVerifiedId } from 'src/utils/eventMappers';
import { triggerHotjarPoll } from 'src/utils/hotjar';
import { constructFromAndToDates } from 'src/utils/utils/buildDates';
import getCandidateEventAttributes from 'src/utils/utils/getCandidateEventAttributes';

import { PAGE_QUERY_PARAM_KEY } from '../../pages/CandidateManagementPage/queryParams';
import { sendCandidateListImpression } from '../../services/jobApplicationService/sendCandidateListImpression';
import type { IJobApplication } from '../../stores/JobApplicationStore/JobApplication';
import {
  type PaginationButtonType,
  trackPaginationClickEvent,
  trackSelectCandidateEvent,
} from '../../utils/events';
import { useStores } from '../../utils/hooks/useStores';
import { JobApplicationCard } from '../CandidateManagementPage/JobApplicationCard';
import { JobApplicationCardSkeleton } from '../CandidateManagementPage/JobApplicationCard/JobApplicationCardSkeleton';
import { ViewportImpressionTracker } from '../CandidateManagementPage/JobApplicationCard/ViewportImpressionTracker';
import type { UseHandleStatusChangeResponse } from '../hooks/useHandleStatusChange';

import translations from './.vocab';
import { OldJobEmptyState } from './OldJobEmptyState';
import { RRError } from './RRError';
import { RecruiterNetworkAlert } from './RecruiterNetworkAlert';
import { hasVerifiedCredentials } from './hasVerifiedCredentials';

import * as styles from './JobApplicationListSection.css';

const triggerMQHotjarPoll = (jobApplication: IJobApplication) => {
  if (jobApplication.sortedMatchedQualities.length > 0) {
    triggerHotjarPoll('cm_matched_qualities_poll');
  }
};

const useTracking = ({
  filtersEventCaptureData,
}: {
  filtersEventCaptureData: FiltersEventCaptureData;
}) => {
  const store = useStores();
  const { jobApplicationStore, jobStore, userStore } = store;

  const trackScrollInView = (jobApplication: IJobApplication, rank: number) => {
    sendCandidateListImpression({
      rank,
      store,
      filters: filtersEventCaptureData,
      jobStore,
      userStore,
      jobApplication,
      jobApplicationStore,
    });
  };

  const trackPageChangeClick = (
    page: number,
    buttonType: PaginationButtonType,
  ) => {
    const attributes = {
      pageNumber: page,
      bucket: jobApplicationStore.bucket?.toLowerCase(),
      prospectInBucketCount: jobApplicationStore.currentTotalOfJobApplications,
    };

    const lastPage = page === jobApplicationStore.numberOfPages;
    trackPaginationClickEvent(buttonType, lastPage, attributes);
  };

  return { trackScrollInView, trackPageChangeClick };
};

interface JobApplicationsListContentProps {
  onChangeApplicationCardState: (
    applicationCardIsExpanded: boolean | undefined,
  ) => void;
  resetFilters: FilterFns['resetFilters'];
  isMustHaveToggleSelected: boolean;
  filtersEventCaptureData: FiltersEventCaptureData;
  handleStatusChange: UseHandleStatusChangeResponse['handleStatusChange'];
  refetchFacets: () => Promise<void>;
  homeLocationIdsForTracking: string[];
  nationalityIdsForTracking: string[];
}

const JobApplicationsListContent: FC<JobApplicationsListContentProps> =
  observer((props) => {
    const {
      onChangeApplicationCardState,
      resetFilters,
      isMustHaveToggleSelected,
      filtersEventCaptureData,
      handleStatusChange,
      refetchFacets,
      homeLocationIdsForTracking,
      nationalityIdsForTracking,
    } = props;

    const store = useStores();
    const {
      jobApplicationStore,
      isMobile,
      userStore,
      jobStore,
      seekAdvertiserId,
    } = store;
    const { questionnaire } = jobStore;
    const { locale, language } = useAppConfig();

    const { trackScrollInView, trackPageChangeClick } = useTracking({
      filtersEventCaptureData,
    });
    const showToast = useToast();

    const { showDeepLinkedDetailView } = useDeepLinkedDetailView();

    const [, setPageQueryString] = useQueryParam(
      PAGE_QUERY_PARAM_KEY,
      NumberParam,
    );

    const { getApplicationCardState } = useUserActionStatus(
      'application-card-state',
    );
    const applicationCardState = getApplicationCardState();

    if (jobApplicationStore.isLoading || !applicationCardState) {
      return (
        <Box
          className={styles.listContent}
          data={{ testid: 'application-card-skeleton' }}
        >
          {[1, 2, 3, 4, 5].map((key) => (
            <JobApplicationCardSkeleton key={key} />
          ))}
        </Box>
      );
    }

    const currentPageApplications: IJobApplication[] =
      jobApplicationStore.currentPageJobApplications;

    if (currentPageApplications.length === 0) {
      return (
        <EmptyState
          bucket={jobApplicationStore.bucket}
          currentPreFilterTotalOfJobApplications={
            jobApplicationStore.currentPreFilterTotalOfJobApplications
          }
          isMustHaveToggleSelected={isMustHaveToggleSelected}
          job={{
            jobId: jobStore.job.jobInformation.jobId!,
            isActive: jobStore.job.isActive,
            hasNoCandidates:
              jobApplicationStore.bucketStatsStore.bucketStats.hasNoCandidates,
          }}
          resetFilters={resetFilters}
          searchText={jobApplicationStore.lastResultSearchText}
        />
      );
    }

    const handlePageChange = (
      page: number,
      buttonType: PaginationButtonType,
    ) => {
      trackPageChangeClick(page, buttonType);
      setPageQueryString(page, SUPPORT_BACK_BUTTON);
    };

    const createOpenDetailsDrawerWithJobAppId =
      (jobApplicationId: string) =>
      (tab: JOB_APPLICATION_TAB, applicationCardIsExpanded?: boolean) => {
        onChangeApplicationCardState(applicationCardIsExpanded);
        showDeepLinkedDetailView({
          jobApplicationId,
          tab,
        });
      };

    const createOnStatusChangeWithJobApplication =
      (jobApplication: IJobApplication) =>
      (bucket: Bucket, isExpanded?: boolean) =>
        handleStatusChange({
          store,
          jobApplicationStore,
          jobApplication,
          destinationBucket: bucket,
          statusChangeLocation: STATUS_CHANGE_LOCATION.CANDIDATE_LIST,
          isExpanded,
          showToast,
          onUpdateSuccess: refetchFacets,
        });

    const createOnClickRating =
      ({ rating: ratingStore }: IJobApplication) =>
      (selectedRatingValue: number) => {
        if (selectedRatingValue === ratingStore.rating) {
          const newRatingValue = 0;
          ratingStore.updateRating(newRatingValue, TRACKING_CANDIDATE_LIST);
          return newRatingValue;
        }
        ratingStore.updateRating(selectedRatingValue, TRACKING_CANDIDATE_LIST);
        return selectedRatingValue;
      };

    return (
      <>
        {jobApplicationStore.hasRRErrorOnCurrentPage && <RRError />}

        <Box className={styles.listContent}>
          {currentPageApplications.map((jobApplication, index) => {
            const openDetailsDrawer = createOpenDetailsDrawerWithJobAppId(
              jobApplication.id,
            );

            const firstWorkHistory =
              jobApplication.profile?.workHistory?.length &&
              jobApplication.profile?.workHistory?.length > 0
                ? jobApplication.profile?.workHistory[0]
                : undefined;
            const education =
              jobApplication.profile?.education?.length &&
              jobApplication.profile?.education?.length > 0
                ? jobApplication.profile?.education[0]
                : undefined;
            const homeLocation =
              jobApplication.profile?.homeLocation ?? undefined;
            const getLastRole = () => {
              if (firstWorkHistory) {
                return {
                  company: firstWorkHistory.company.text,
                  title: firstWorkHistory.title.text,
                  from: {
                    month: firstWorkHistory.startDate.month,
                    year: firstWorkHistory.startDate.year,
                  },
                  to: firstWorkHistory.endDate
                    ? {
                        month: firstWorkHistory.endDate.month,
                        year: firstWorkHistory.endDate.year,
                      }
                    : undefined,
                };
              }

              if (
                Boolean(jobApplication.mostRecentCompanyName) ||
                Boolean(jobApplication.mostRecentJobTitle)
              ) {
                const { from, to } = constructFromAndToDates(
                  jobApplication.mostRecentRoleMonths || 0,
                );
                return {
                  company: jobApplication.mostRecentCompanyName,
                  title: jobApplication.mostRecentJobTitle,
                  from,
                  to,
                };
              }

              return undefined;
            };

            const roleRequirements = mapToSimplifiedAnswers({
              questionnaire,
              roleRequirements: jobApplication.roleRequirements,
            });

            const profile = jobApplication.profile
              ? transformProfileToComponentProps(jobApplication.profile)
              : null;

            const certsyVerifiedFullName =
              jobApplication.profile?.verifiedIdentity?.fullName;

            const isVerifiedId = convertToIsVerifiedId(jobApplication.profile);

            const { getFilterDrawerState } = useUserActionStatus(
              'filter-drawer-state',
            );

            const isFilterExpand = getFilterDrawerState() === 'expanded';

            return (
              <ViewportImpressionTracker
                key={jobApplication.id}
                handleSendImpression={() =>
                  trackScrollInView(jobApplication, index + 1)
                }
              >
                <JobApplicationCard
                  shouldShowNewBadge={jobApplication.isUnread}
                  shouldShowRelevantApplicationBadge={
                    jobApplication.billableStatus === 'BILLABLE'
                  }
                  shouldShowVerifiedCredentialsBadge={hasVerifiedCredentials({
                    roleRequirements,
                    profile,
                  })}
                  currentBucket={jobApplicationStore.bucket}
                  attachments={jobApplication.attachments.map((a) => ({
                    id: a.id,
                    type: a.type,
                  }))}
                  jobId={`${jobStore.job.jobInformation.jobId}`}
                  userId={userStore.user.userId}
                  seekAdvertiserId={seekAdvertiserId}
                  jobApplication={{
                    id: jobApplication.id,
                    applicationCorrelationId:
                      jobApplication.applicationCorrelationId,
                    candidateId: jobApplication.candidateId,
                    fullName: jobApplication.fullName,
                    isUnread: jobApplication.isUnread,
                    appliedDate: jobApplication.appliedDate,
                    source: jobApplication.source,
                    rating: jobApplication.rating.rating,
                  }}
                  education={
                    education
                      ? {
                          name: education.name.text,
                          institute: education.institute.text,
                          completionYear: education.completionDate?.year,
                        }
                      : undefined
                  }
                  lastRole={getLastRole()}
                  applicationListState={applicationCardState}
                  roleRequirements={roleRequirements}
                  matchedQualities={{
                    qualities: (
                      jobApplication.matchedQualities.qualities || []
                    ).map((quality) => ({
                      id: quality.id,
                      displayLabel: quality.displayLabel,
                      isTop: quality.isTop,
                    })),
                    hasError: jobApplication.matchedQualities.hasError,
                  }}
                  certsyVerifiedFullName={certsyVerifiedFullName}
                  emailCount={jobApplication.prospectEmailStore.emailCount}
                  previousApplicationCount={
                    jobApplication.candidateStore.previousApplicationCount
                  }
                  noteCount={jobApplication.notes.noteCount}
                  checkboxProps={{
                    checked: jobApplication.selected,
                    onChange: jobApplication.toggleSelect,
                  }}
                  openDetailsDrawer={openDetailsDrawer}
                  openDetailsDrawerProfileTab={(
                    compactCardIsExpanded?: boolean,
                  ) => {
                    trackSelectCandidateEvent({
                      ...getCandidateEventAttributes(jobApplication, isMobile),
                      isVerifiedId,
                      currentPage: TRACKING_CANDIDATE_DETAILS,
                      searchLocationId: homeLocationIdsForTracking,
                      searchNationalityId: nationalityIdsForTracking,
                    });
                    openDetailsDrawer(
                      JOB_APPLICATION_TAB.PROFILE,
                      compactCardIsExpanded,
                    );
                    triggerMQHotjarPoll(jobApplication);
                  }}
                  markApplicationAsRead={() =>
                    jobApplication.markRead({ filtersEventCaptureData })
                  }
                  changeStatus={createOnStatusChangeWithJobApplication(
                    jobApplication,
                  )}
                  onClickRating={createOnClickRating(jobApplication)}
                  locale={locale}
                  homeLocation={homeLocation}
                  isVerifiedId={isVerifiedId}
                  nationalities={profile?.nationalities}
                  language={language}
                  homeLocationIdsForTracking={homeLocationIdsForTracking}
                  nationalityIdsForTracking={nationalityIdsForTracking}
                  isFilterExpand={isFilterExpand}
                />
              </ViewportImpressionTracker>
            );
          })}
        </Box>

        <Box display="flex" justifyContent="center" paddingY="large">
          <Pagination
            currentPage={jobApplicationStore.currentPage}
            totalPages={jobApplicationStore.numberOfPages}
            handlePageChange={handlePageChange}
            isMobile={isMobile}
          />
        </Box>
      </>
    );
  });

export interface JobApplicationListSectionProps {
  actionToolbar: ReactElement;
  openRelevantApplicationDialog: () => void;
  onChangeApplicationCardState: (
    applicationCardIsExpanded: boolean | undefined,
  ) => void;
  useFiltersFns: FilterFns;
  isMustHaveToggleSelected: boolean;
  filtersEventCaptureData: FiltersEventCaptureData;
  handleStatusChange: UseHandleStatusChangeResponse['handleStatusChange'];
  refetchFacets: () => Promise<void>;
  homeLocationIdsForTracking: string[];
  nationalityIdsForTracking: string[];
  jobPlacementAlert: ReactElement;
}

const JobApplicationListSection: FC<JobApplicationListSectionProps> = ({
  actionToolbar,
  openRelevantApplicationDialog,
  onChangeApplicationCardState,
  useFiltersFns,
  isMustHaveToggleSelected,
  filtersEventCaptureData,
  handleStatusChange,
  refetchFacets,
  homeLocationIdsForTracking,
  nationalityIdsForTracking,
  jobPlacementAlert,
}) => {
  const { t } = useTranslations(translations);
  const { jobApplicationStore, jobStore } = useStores();
  const { currentPageJobApplications, numberOfPages } = jobApplicationStore;
  const { getBillableAlertStatus, hideBillableAlert } =
    useUserActionStatus('billable-alert');
  const { getRecruiterNetworkAlertStatus, hideRecruiterNetworkAlert } =
    useUserActionStatus('recruiter-network-alert');

  const [, setPageQueryString] = useQueryParam(
    PAGE_QUERY_PARAM_KEY,
    NumberParam,
  );

  useEffect(() => {
    // all job apps removed on last page, move to previous page
    if (numberOfPages >= 1 && jobApplicationStore.currentPage > numberOfPages) {
      setPageQueryString(numberOfPages, SUPPORT_BACK_BUTTON);
    }
  }, [jobApplicationStore, numberOfPages, setPageQueryString]);

  useEffect(() => {
    debounce(() => {
      const { certsyEventCaptureInfo } = jobApplicationStore;

      sendCandidateListView({
        jobApplications: currentPageJobApplications,
        ...certsyEventCaptureInfo,
      });
    }, 500)();
  }, [currentPageJobApplications, jobApplicationStore]);

  const srnAlert = (
    <RecruiterNetworkAlert
      hideAlert={hideRecruiterNetworkAlert}
      categoryId={jobStore.job.jobInformation.categoryId}
      jobCreatedDate={jobStore.job.jobInformation.jobCreatedDate}
      locationIds={jobStore.job.jobInformation.locationIds}
    />
  );

  return (
    <Box data-cy="job-application-list" width="full">
      <Stack space="medium">
        {jobStore.job.jobInformation.billableApplicants &&
        getBillableAlertStatus() === 'visible' &&
        jobApplicationStore.currentPage === 1 ? (
          <Alert
            tone="promote"
            onClose={hideBillableAlert}
            closeLabel={t('Close')}
          >
            <Text>
              {t(
                "<Strong>Relevant applications</Strong> are marked with a badge. Your Premium ad's price will be reduced if we do not deliver on the committed number of relevant applications. <TextButtonLink>Learn more</TextButtonLink>",
                {
                  Strong: (children) => <Strong>{children}</Strong>,
                  TextButtonLink: (children) => (
                    <TextLinkButton onClick={openRelevantApplicationDialog}>
                      {children}
                    </TextLinkButton>
                  ),
                },
              )}
            </Text>
          </Alert>
        ) : null}

        {jobApplicationStore.currentPage === 1 &&
          getRecruiterNetworkAlertStatus() === 'visible' &&
          jobApplicationStore.bucket === BUCKETS.INBOX &&
          srnAlert}

        {jobPlacementAlert}

        <Box>
          {jobApplicationStore.currentPageJobApplications.length > 0
            ? actionToolbar
            : null}

          {jobStore.job.showOldJobIntercom && <OldJobEmptyState />}
          {!jobStore.job.showOldJobIntercom && (
            <JobApplicationsListContent
              handleStatusChange={handleStatusChange}
              onChangeApplicationCardState={onChangeApplicationCardState}
              resetFilters={useFiltersFns.resetFilters}
              isMustHaveToggleSelected={isMustHaveToggleSelected}
              filtersEventCaptureData={filtersEventCaptureData}
              refetchFacets={refetchFacets}
              homeLocationIdsForTracking={homeLocationIdsForTracking}
              nationalityIdsForTracking={nationalityIdsForTracking}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default observer(JobApplicationListSection);
